import { useRef, useState, useEffect } from "react";

/**
 * TwoFactorPage
 * 6-digit code entry used for signup, social login, and secret-reveal flows.
 *
 * The code is frequently copied from a mail client or pasted from an SMS
 * autofill, so a paste of the whole code fills every box rather than only the
 * one that received the event — a single-character `maxLength` used to make a
 * pasted code look like one digit, which is the most common complaint about this
 * screen, and the loudest on mobile.
 *
 * Props:
 *   destination — display string, e.g. "+1 •••• ••22"
 *   resendSeconds — countdown before resend is enabled (default 24)
 *   onVerify(code: string)
 *   onResend()
 */
export default function TwoFactorPage({ destination = "+1 •••• ••22", resendSeconds = 24, onVerify, onResend }) {
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [seconds, setSeconds] = useState(resendSeconds);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  // fill distributes every digit it is given across the boxes, starting at
  // `from`. Handlers that receive one digit and handlers that receive six share
  // it, which is what keeps typing and pasting consistent.
  function fill(from, raw) {
    const incoming = String(raw ?? "").replace(/[^0-9]/g, "");
    if (!incoming) return;
    const next = [...digits];
    let cursor = from;
    for (const character of incoming) {
      if (cursor > 5) break;
      next[cursor] = character;
      cursor += 1;
    }
    setDigits(next);
    // Focus follows the last box written, clamped to the field: pasting six
    // digits leaves the caret on the last one rather than off the end.
    inputsRef.current[Math.min(cursor, 5)]?.focus();
  }

  function handleChange(i, value) {
    // A paste is handled by onPaste, but the browser's one-time-code autofill
    // and some mobile keyboards deliver the whole code through a change event.
    fill(i, value);
  }

  function handlePaste(i, event) {
    const text = event.clipboardData?.getData("text") ?? "";
    if (!/[0-9]/.test(text)) return;
    event.preventDefault();
    // A complete code pasted into any box fills the whole field: a user who
    // copies "123456" and happens to tap the third box still gets 123456.
    const complete = text.replace(/[^0-9]/g, "").length >= 6;
    fill(complete ? 0 : i, text);
  }

  function handleKeyDown(i, e) {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputsRef.current[i + 1]?.focus();
  }

  function handleSubmit(e) {
    e.preventDefault();
    onVerify?.(digits.join(""));
  }

  function handleResend() {
    setSeconds(resendSeconds);
    onResend?.();
  }

  return (
    <div style={{ maxWidth: 360, margin: "0 auto", background: "var(--surface-1)", borderRadius: "var(--radius-lg)", padding: "2rem", textAlign: "center" }}>
      <p style={{ fontSize: "var(--text-lg)", fontWeight: 500, margin: "12px 0 4px" }}>Enter your code</p>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 22px" }}>
        We sent a 6-digit code to {destination}
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 18 }}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              className="ical-input"
              // The first box takes the browser's one-time-code autofill; the
              // rest must not each claim to be the code field.
              name={i === 0 ? "one-time-code" : undefined}
              autoComplete={i === 0 ? "one-time-code" : "off"}
              maxLength={1}
              inputMode="numeric"
              aria-label={`Digit ${i + 1} of 6`}
              style={{ width: 40, height: 48, textAlign: "center", fontSize: "var(--text-lg)" }}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onPaste={(e) => handlePaste(i, e)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              // Selecting on focus means typing over a filled box replaces it
              // rather than being swallowed by maxLength.
              onFocus={(e) => e.target.select()}
            />
          ))}
        </div>

        <button type="submit" className="ical-btn ical-btn-primary" style={{ width: "100%", marginBottom: 12 }}>
          Verify
        </button>
      </form>

      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: 0 }}>
        Didn't get it?{" "}
        {seconds > 0 ? (
          <span>Resend in 0:{String(seconds).padStart(2, "0")}</span>
        ) : (
          <button type="button" onClick={handleResend} style={{ color: "var(--text-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}>
            Resend code
          </button>
        )}
      </p>
    </div>
  );
}
